/**
 * Codec selection for MediaRecorder.
 *
 * VP9 first: it is roughly 30% smaller than VP8 at the same quality on screen
 * content, which is what we are almost always recording. VP8 is the fallback
 * for older Chromium and Firefox builds. Safari's MediaRecorder emits MP4/AAC
 * and does not support WebM at all, so it gets its own branch — the API
 * accepts video/mp4 as an upload content type, so nothing downstream cares.
 */

export interface CodecChoice {
  /** Passed to MediaRecorder. */
  mimeType: string;
  /** Passed to POST /v1/videos — the API only knows base types. */
  contentType: 'video/webm' | 'video/mp4';
  /** Shown in the recorder's diagnostics line. */
  label: string;
}

const CANDIDATES: CodecChoice[] = [
  { mimeType: 'video/webm;codecs=vp9,opus', contentType: 'video/webm', label: 'WebM · VP9 / Opus' },
  { mimeType: 'video/webm;codecs=vp8,opus', contentType: 'video/webm', label: 'WebM · VP8 / Opus' },
  { mimeType: 'video/webm', contentType: 'video/webm', label: 'WebM · browser default' },
  { mimeType: 'video/mp4;codecs=avc1,mp4a.40.2', contentType: 'video/mp4', label: 'MP4 · H.264 / AAC' },
  { mimeType: 'video/mp4', contentType: 'video/mp4', label: 'MP4 · browser default' },
];

export function pickCodec(): CodecChoice | null {
  if (typeof MediaRecorder === 'undefined') return null;
  for (const candidate of CANDIDATES) {
    if (MediaRecorder.isTypeSupported(candidate.mimeType)) return candidate;
  }
  // Some browsers report nothing as supported but still record with the
  // default. Only claim this when MediaRecorder exists at all.
  return { mimeType: '', contentType: 'video/webm', label: 'browser default' };
}

/** Does this browser have everything the recorder needs? */
export interface CaptureSupport {
  displayCapture: boolean;
  mediaRecorder: boolean;
  canvasCapture: boolean;
  /** True when we can record at all. */
  ok: boolean;
}

export function detectSupport(): CaptureSupport {
  const displayCapture =
    typeof navigator !== 'undefined' &&
    typeof navigator.mediaDevices?.getDisplayMedia === 'function';
  const mediaRecorder = typeof MediaRecorder !== 'undefined';
  const canvasCapture =
    typeof HTMLCanvasElement !== 'undefined' &&
    typeof HTMLCanvasElement.prototype.captureStream === 'function';

  return {
    displayCapture,
    mediaRecorder,
    canvasCapture,
    ok: displayCapture && mediaRecorder,
  };
}
